import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ContentShell } from "@/components/ContentShell";
import { Seo } from "@/components/Seo";

const fmt = (n) => "$" + Math.round(n).toLocaleString();

function Calc() {
  const [spend, setSpend] = useState(45000);
  const [cases, setCases] = useState(12);
  const cps = cases > 0 ? spend / cases : 0;
  const fields = [
    { label: "Monthly marketing cost (ad spend + agency fees)", val: spend, set: setSpend, min: 2000, max: 300000, step: 1000, disp: fmt(spend), tid: "cps-spend" },
    { label: "Signed / retained cases per month", val: cases, set: setCases, min: 1, max: 120, step: 1, disp: cases, tid: "cps-cases" },
  ];
  return (
    <div className="border border-white/10 bg-[#12324D] p-6 sm:p-8" data-testid="cps-calculator">
      <p className="font-mono-data text-[11px] uppercase tracking-widest text-[#D5A44A]">Cost per signed case calculator</p>
      <div className="mt-6 space-y-6">
        {fields.map((f) => (
          <div key={f.tid}>
            <div className="flex justify-between text-sm mb-2"><span className="text-zinc-400">{f.label}</span><span className="font-mono-data text-white" data-testid={f.tid + "-val"}>{f.disp}</span></div>
            <input type="range" min={f.min} max={f.max} step={f.step} value={f.val} onChange={(e) => f.set(Number(e.target.value))} className="w-full accent-[#D5A44A]" data-testid={f.tid} />
          </div>
        ))}
      </div>
      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        <div className="border border-[#D5A44A]/30 bg-[#D5A44A]/5 p-4">
          <p className="font-mono-data text-[9px] uppercase tracking-widest text-[#D5A44A]">Cost per signed case</p>
          <p className="font-display text-3xl text-[#D5A44A] mt-1" data-testid="cps-result">{fmt(cps)}</p>
        </div>
        <div className="border border-white/10 bg-black/30 p-4">
          <p className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-500">Annual marketing cost</p>
          <p className="font-display text-3xl text-white mt-1" data-testid="cps-annual">{fmt(spend * 12)}</p>
        </div>
      </div>
      <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">Illustrative estimate only. A true figure includes attributable intake cost and is measured across your firm's actual sales cycle. Not evidence of savings or recoveries.</p>
    </div>
  );
}

export default function CostPerSignedCase() {
  const seo = (
    <Seo
      title="Cost Per Signed Case: The Only Marketing Metric That Matters | RetainProof"
      description="Cost per signed case ties marketing spend to actual retained clients. Learn how to calculate it, why cost-per-lead misleads, and how an independent review verifies it for law firms."
      path="/cost-per-signed-case"
    />
  );
  return (
    <ContentShell seo={seo} ctaTitle="Find out your real cost per signed case.">
      <section className="max-w-5xl mx-auto px-6 pt-36 pb-14">
        <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-5">The metric that matters</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-4xl" data-testid="cps-title">
          Cost per signed case: the only marketing number tied to signed clients.
        </h1>
        <p className="mt-6 text-lg text-zinc-400 max-w-3xl leading-relaxed">
          Clicks, impressions, calls and even leads can all rise while your signed-case volume stays flat. Cost per signed case (sometimes called cost per retained case) is the one metric that connects what you spend to the clients you actually retain — and it's the number most agency dashboards never show.
        </p>
      </section>

      <section className="border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-5xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">How to calculate it</h2>
            <div className="mt-6 border border-white/10 bg-black/30 p-6 font-mono-data text-sm text-zinc-200 leading-relaxed">
              Cost per signed case =<br />
              <span className="text-[#D5A44A]">total marketing cost</span> ÷ <span className="text-[#0E7C7B]">signed cases</span>
            </div>
            <p className="mt-6 text-zinc-400 leading-relaxed text-sm">
              "Total marketing cost" means everything: ad spend, agency and management fees, lead-purchase costs, and the attributable share of intake staff time. "Signed cases" are matters that produced a signed retainer — measured across your firm's real sales cycle (often 90–180 days for personal injury), not the calendar month the click happened in.
            </p>
          </div>
          <Calc />
        </div>
      </section>

      <section className="border-t border-white/10" data-testid="cps-why-cpl">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">Why cost-per-lead misleads law firms</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            A vendor can deliver cheap leads all day. If those leads don't sign, low cost-per-lead simply hides a high cost per signed case. Industry benchmarks put personal-injury lead costs anywhere from roughly $200 to $700+ per qualified lead — but the number that decides profitability is what each <span className="text-white">retained</span> case costs after intake conversion. Two vendors with identical cost-per-lead can differ by thousands per signed case once you follow the leads through to retainer.
          </p>
          <p className="mt-4 text-zinc-500 text-sm leading-relaxed">
            Cost-per-lead ranges cited from published 2026 legal marketing benchmarks (<a href="https://madeforlaw.com/guides/cost-per-lead-benchmarks-2026" target="_blank" rel="noopener noreferrer" className="text-[#D5A44A] hover:underline">MadeForLaw, 2026</a>). Figures reflect the broader market, not RetainProof clients.
          </p>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">Why most firms can't calculate it — and how it gets verified</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Calculating cost per signed case requires closed-loop attribution: every lead source tagged, call tracking in place, and each matter's original source carried through intake to a "retained" status in your CRM. When that chain is broken — or when the only reporting comes from the vendor being paid — the number is difficult to verify. An independent review reconciles your spend, invoices, lead data and retained matters to calculate it from a neutral vantage point.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <Link to="/law-firm-marketing-audit" className="inline-flex items-center gap-1.5 text-[#D5A44A] hover:text-[#e6bd6f] group" data-testid="cps-audit-link">
              What an independent marketing audit reviews <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/insights/marketing-attribution-for-law-firms" className="inline-flex items-center gap-1.5 text-[#D5A44A] hover:text-[#e6bd6f] group" data-testid="cps-attribution-link">
              How marketing attribution works for law firms <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </ContentShell>
  );
}
